import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X, Export, ArrowsOut, Monitor, DeviceMobile } from "@phosphor-icons/react";
import { useFullscreen, useIsIosSafari } from "@/hooks/useFullscreen";
import { useIsTabletOrBelow } from "@/hooks/useMediaQuery";

const IOS_HINT_KEY = "dispatch911_ios_install_dismissed_at";
const FS_PROMPT_KEY = "dispatch911_fs_prompt_choice";
const IOS_HINT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * IosInstallHint — small banner that nudges iOS Safari users to add the
 * app to their home screen for a true fullscreen / PWA experience. Hidden
 * automatically when:
 *   • Not iOS Safari (Android, desktop, Chrome iOS, already-installed PWA)
 *   • User dismissed it in the last 30 days
 */
export function IosInstallHint() {
  const isIosSafari = useIsIosSafari();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!isIosSafari) { setDismissed(true); return; }
    const stored = localStorage.getItem(IOS_HINT_KEY);
    if (!stored) { setDismissed(false); return; }
    const t = parseInt(stored, 10);
    if (Number.isFinite(t) && Date.now() - t < IOS_HINT_TTL_MS) {
      setDismissed(true);
    } else {
      setDismissed(false);
    }
  }, [isIosSafari]);

  if (!isIosSafari || dismissed) return null;

  const close = () => {
    localStorage.setItem(IOS_HINT_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid="ios-install-hint"
      className="fixed left-2 right-2 top-[64px] z-[55] border border-amber-400/40 bg-amber-500/10 backdrop-blur px-3 py-2 flex items-center gap-2"
      style={{ animation: "slideDown 0.3s ease-out" }}
    >
      <Export size={16} className="text-amber-300 shrink-0" weight="bold" />
      <span className="mono text-[10px] uppercase tracking-widest text-amber-100 flex-1 leading-tight">
        Tap <span className="text-amber-300">Share</span> →{" "}
        <span className="text-amber-300">"Add to Home Screen"</span> for fullscreen mode.
      </span>
      <button
        type="button"
        data-testid="ios-install-hint-close"
        onClick={close}
        className="w-6 h-6 inline-flex items-center justify-center hover:bg-amber-500/15 text-amber-300"
        aria-label="Dismiss"
      >
        <X size={12} />
      </button>
      <style>{`@keyframes slideDown { from { transform: translateY(-8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

/**
 * LaunchFullscreenPrompt — shown ONCE on first game launch on a device
 * that supports the Fullscreen API. Persists the user's choice in
 * localStorage so we never ask again on that device. iOS Safari users
 * see this too, since the API does work on 16.4+, but they also get the
 * IosInstallHint above which is the more reliable path.
 *
 * Trigger conditions (all must be true):
 *   • Fullscreen API is supported by this browser
 *   • App is not already in PWA standalone mode (already fullscreen)
 *   • Not currently in fullscreen
 *   • Mobile/tablet (we don't bug desktop users — they have F11)
 *   • No prior choice stored
 */
export function LaunchFullscreenPrompt() {
  const { supported, isStandalonePWA, isFullscreen, enter } = useFullscreen();
  const isSmall = useIsTabletOrBelow();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!supported || isStandalonePWA || isFullscreen) return;
    if (!isSmall) return;
    const prior = localStorage.getItem(FS_PROMPT_KEY);
    if (prior) return;
    // Slight delay so the prompt appears after the HUD animates in.
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [supported, isStandalonePWA, isFullscreen, isSmall]);

  const accept = async () => {
    localStorage.setItem(FS_PROMPT_KEY, "yes");
    setOpen(false);
    await enter();
  };

  const decline = () => {
    localStorage.setItem(FS_PROMPT_KEY, "no");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && decline()}>
      <DialogContent
        data-testid="launch-fullscreen-prompt"
        className="rounded-none border border-red-500/30 bg-[#0F1115] text-white max-w-sm p-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[11px] text-zinc-300 flex items-center gap-2">
            <ArrowsOut size={13} className="text-red-400" /> Enter Fullscreen?
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Play Dispatch 911 full-screen for a better view of the map and call list.
          </p>
          <p className="mono text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed">
            You can always toggle this from the user menu (top-right).
          </p>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="fs-prompt-decline"
              onClick={decline}
              variant="outline"
              className="flex-1 rounded-none h-9 mono uppercase text-[10px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5"
            >
              Not Now
            </Button>
            <Button
              data-testid="fs-prompt-accept"
              onClick={accept}
              className="flex-1 rounded-none h-9 mono uppercase text-[10px] tracking-widest bg-red-600 hover:bg-red-500 text-white border border-red-500"
            >
              <ArrowsOut size={12} className="mr-1.5" /> Go Fullscreen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * FullscreenMenuItem — convenience: renders nothing if the browser can't
 * do fullscreen at all OR we're already in a standalone PWA shell (so
 * we don't show a useless menu item). Otherwise renders an icon + label.
 * Used inside the user dropdown in HUD.
 */
export function useFullscreenMenu() {
  const { supported, isFullscreen, isStandalonePWA, toggle } = useFullscreen();
  if (!supported || isStandalonePWA) return null;
  return {
    label: isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen",
    Icon: isFullscreen ? Monitor : ArrowsOut,
    onClick: () => toggle(),
    isFullscreen,
  };
}

// Re-export for convenience in HUD
export { DeviceMobile };
